import { TenantType } from 'customers/tenants-common-view/lib/schema/Tenant.types';
import {
  adaptDBServerOSDetailsResponse,
  adaptDBServersResponse,
} from './adaptDBServersResponse';
import { useEffect, useMemo, useState } from 'react';
import { useLazyGetCallWithURL } from 'common/api/useApiCall';
import { URLS } from 'constants/URL';
import { ITEMS_PER_PAGE } from 'constants/appConstants';
import { useNavigate } from 'react-router-dom';
import { SUBSCRIPTION_STATUS_FOR_FILTER } from 'customers/db-services-common-view/components/DBServices/constants';
import { getIdfromSubscriptionIdToNameMap } from 'customers/db-services-common-view/components/DBServices/utils';

type DBServer = {
  tenant: TenantType;
  subscriptionId?: any;
};

export const typeOfRequest = 'DBSERVER';

export const useDBServer = (props: DBServer) => {
  const { tenant, subscriptionId } = props;

  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [computeAnchor, setComputeAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState({});

  const [filters, setFilters] = useState<any>({
    subscription: tenant?.subscriptions?.find(
      (_s) => _s?._id === subscriptionId,
    )?.name,
  });

  const subscriptionIdToNameMap = {};
  const subs = new Set();
  tenant?.subscriptions
    ?.filter((sub) => SUBSCRIPTION_STATUS_FOR_FILTER.includes(sub?.status))
    .forEach((sub) => {
      subscriptionIdToNameMap[sub?._id] = sub?.name;
      subs.add(sub?.name);
    });

  const subsList = Array.from(subs)?.map((sub) => ({ id: sub, name: sub }));

  const {
    response: dBServersResponse,
    isLoading: dBServersLoading,
    postData: fetchDBServers,
  } = useLazyGetCallWithURL(adaptDBServersResponse);

  const {
    response: dBServerCountResponse,
    isLoading: dBServersCountIsLoading,
    postData: fetchDBServersCount,
  } = useLazyGetCallWithURL();

  const createParams = () => {
    const _subscriptionId = getIdfromSubscriptionIdToNameMap(
      subscriptionIdToNameMap,
      filters?.subscription,
    );

    const _params = Object.keys(filters)?.reduce(
      (acc, key) =>
        key === 'subscription'
          ? filters[key] && _subscriptionId
            ? `${acc}&subscription-id=${_subscriptionId}`
            : ''
          : acc
          ? `${acc}${
              filters[key]?.toString()?.toLowerCase() !== 'all'
                ? `&${key}=${filters[key]}`
                : ''
            }`
          : `${
              filters[key]?.toString()?.toLowerCase() !== 'all'
                ? `${key}=${filters[key]}`
                : ''
            }`,
      '',
    );
    return (
      _params +
      (tenant ? (_params ? '&' : '') + `tenant-id=${tenant?._id}` : '')
    );
  };

  useEffect(() => {
    fetchDBServers(
      `${URLS.fetchComputes}page-size=${ITEMS_PER_PAGE}&page-offset=${
        page - 1
      }&${createParams()}`,
    );
    fetchDBServersCount(
      `${URLS.fetchComputesCount}page-size=${ITEMS_PER_PAGE}&page-offset=${
        page - 1
      }&${createParams()}`,
    );
  }, [page, filters]);

  const {
    response: osDetailsResponse,
    isLoading: osDetailsIsloading,
    postData: fetchOSDetails,
  } = useLazyGetCallWithURL(adaptDBServerOSDetailsResponse);

  const dbServers = useMemo(() => {
    return dBServersResponse || [];
  }, [dBServersResponse]);

  const dbServersCount = useMemo(() => {
    return dBServerCountResponse?.response?.count;
  }, [dBServerCountResponse]);

  const dbServersPageCount = useMemo(
    () => Math.ceil(dbServersCount / ITEMS_PER_PAGE) || 0,
    [dbServersCount],
  );

  const handlePaginationChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
  };

  useEffect(() => {
    if (dbServers?.length) {
      const computeIds = dbServers?.map((compute) => compute.id);
      fetchOSDetails(
        `${URLS.fetchOSInfo}compute-resource-ids=${computeIds?.join(
          ',',
        )}&tenant-domain=${tenant?.pgSource}`,
      );
    }
  }, [dBServersResponse]);

  const osInfo = useMemo(() => {
    return osDetailsResponse || {};
  }, [osDetailsResponse]);

  const handleRowClick = (params) => {
    const { row: data } = params;
    const { id } = data;
    const url = `/tenants/${tenant?.pgSource}/${tenant?._id}/database-servers/${id}/overview`;
    navigate(url);
  };
  const handleFilterChange = (filterKey, val) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [filterKey]: val }));
  };

  return {
    filters,
    subsList,
    page,
    dbServersCount,
    computeAnchor,
    isLoading: dBServersLoading || dBServersCountIsLoading,
    selectedRow,
    dbServersPageCount,
    dbServers,
    osInfo,
    osDetailsIsloading,
    setComputeAnchor,
    setSelectedRow,
    handlePaginationChange,
    handleRowClick,
    handleFilterChange,
  };
};
